import React, { useEffect, useRef, useState } from 'react';
import { inbox as inboxApi } from '@/utils/api';
import { Mail, MessageSquare, Send, Inbox as InboxIcon, AlertCircle, Users, RefreshCw, Link2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Inbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [projectsForAssign, setProjectsForAssign] = useState([]);
  const [assignTarget, setAssignTarget] = useState('');
  const listEndRef = useRef(null);

  const loadThreads = async () => {
    try {
      const r = await inboxApi.threads();
      setThreads(r.data);
    } finally {
      setLoading(false);
    }
  };
  const loadMessages = async (key) => {
    const r = await inboxApi.messages(key);
    setMessages(r.data);
    setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.key);
    // Default the "also send email" toggle to ON when the last message in the
    // thread came in via email, OFF for web chat threads.
    setAlsoEmail(active.channel === 'email');
  }, [active]);

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await inboxApi.reply(active.key, reply.trim(), alsoEmail);
      setReply('');
      await loadMessages(active.key);
      await loadThreads();
      toast.success(alsoEmail ? 'Reply sent via email + portal' : 'Reply posted to portal chat');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const openAssign = async () => {
    const r = await inboxApi.projectsForAssignment();
    setProjectsForAssign(r.data);
    setAssignOpen(true);
  };
  const doAssign = async () => {
    if (!assignTarget) return toast.error('Pick a project');
    const ids = messages.map(m => m.id);
    await inboxApi.assign(ids, assignTarget);
    toast.success('Assigned');
    setAssignOpen(false);
    setAssignTarget('');
    setActive(null);
    loadThreads();
  };

  const totalUnread = threads.reduce((acc, t) => acc + (t.unread_count || 0), 0);

  return (
    <div className="flex h-[calc(100vh-64px)] animate-fade-in" data-testid="inbox-page">
      {/* Left rail — threads */}
      <aside className="w-80 flex-shrink-0 flex flex-col" style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div className="p-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <InboxIcon size={16} style={{ color: '#006080' }} />
              <h1 className="font-black text-base" style={{ color: 'var(--ink)' }}>Inbox</h1>
            </div>
            <button onClick={loadThreads} className="p-1" title="Refresh" data-testid="inbox-refresh">
              <RefreshCw size={13} style={{ color: 'var(--muted)' }} />
            </button>
          </div>
          <p className="text-xs" style={{ color: 'var(--muted)' }}>
            {threads.length} thread{threads.length !== 1 ? 's' : ''} · {totalUnread} unread
          </p>
        </div>

        <div className="flex-1 overflow-y-auto" data-testid="threads-list">
          {loading ? (
            <div className="p-8 text-center">
              <p className="text-sm" style={{ color: 'var(--muted)' }}>Loading…</p>
            </div>
          ) : threads.length === 0 ? (
            <div className="p-8 text-center">
              <Mail size={28} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Inbox zero</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                Project chat messages and inbound emails (once IMAP is configured) will land here.
              </p>
            </div>
          ) : (
            threads.map(t => {
              const isActive = active?.key === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActive(t)}
                  className="w-full text-left p-3 flex gap-2.5 transition-colors"
                  style={{
                    background: isActive ? '#E6F1F4' : 'transparent',
                    borderBottom: '1px solid var(--border)',
                    borderLeft: isActive ? '3px solid #006080' : '3px solid transparent',
                  }}
                  data-testid={`thread-${t.key}`}
                >
                  <div className="w-8 h-8 rounded-btn flex items-center justify-center flex-shrink-0" style={{
                    background: t.needs_assignment ? '#D9A44115' : t.channel === 'email' ? '#5B2D8E15' : '#3C8A5A15',
                  }}>
                    {t.needs_assignment ? <AlertCircle size={13} style={{ color: '#D9A441' }} /> :
                      t.channel === 'email' ? <Mail size={13} style={{ color: '#5B2D8E' }} /> :
                      <MessageSquare size={13} style={{ color: '#3C8A5A' }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm truncate flex-1" style={{ color: 'var(--ink)' }}>{t.project_name}</p>
                      {t.unread_count > 0 && (
                        <span className="badge-status flex-shrink-0" style={{ background: '#006080', color: 'white', fontSize: 10 }}>{t.unread_count}</span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--muted)' }}>
                      {t.client_name}{t.last_sender ? ` · ${t.last_sender}` : ''}
                    </p>
                    <p className="text-xs mt-1 line-clamp-1" style={{ color: 'var(--muted)' }}>{t.preview || '(no content)'}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* Right pane — conversation */}
      <section className="flex-1 flex flex-col" style={{ background: 'var(--bg)' }}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <MessageSquare size={40} style={{ color: 'var(--border)', marginBottom: 12 }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Select a conversation</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Pick a thread on the left to read and reply.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-black text-base" style={{ color: 'var(--ink)' }}>{active.project_name}</h2>
                  <span className="badge-status" style={{
                    background: active.channel === 'email' ? '#5B2D8E15' : '#3C8A5A15',
                    color: active.channel === 'email' ? '#5B2D8E' : '#3C8A5A',
                  }}>{active.channel}</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>{active.client_name}{active.client_email ? ` · ${active.client_email}` : ''}</p>
              </div>
              {active.needs_assignment && (
                <button onClick={openAssign} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-btn font-semibold" style={{ background: '#D9A441', color: 'white' }} data-testid="assign-thread-btn">
                  <Link2 size={12} /> Assign to project
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
              {messages.map((m, i) => {
                const isOperator = m.sender_role === 'super_admin';
                const isInboundEmail = m.sender_role === 'email_inbound';
                return (
                  <div key={m.id || i} className={`flex ${isOperator ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[78%]">
                      <div className="flex items-center gap-1.5 mb-0.5 text-xs" style={{ color: 'var(--muted)' }}>
                        <span className="font-semibold">{m.sender_name}</span>
                        {m.channel === 'email' && <Mail size={9} />}
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                      </div>
                      <div className="p-3 rounded-card" style={{
                        background: isOperator ? '#006080' : isInboundEmail ? 'var(--surface)' : 'var(--surface)',
                        color: isOperator ? 'white' : 'var(--ink)',
                        border: isOperator ? 'none' : '1px solid var(--border)',
                        whiteSpace: 'pre-wrap',
                        fontSize: 13.5,
                        lineHeight: 1.55,
                      }}>
                        {m.content}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={listEndRef} />
            </div>

            {/* Reply composer */}
            {!active.needs_assignment && (
              <div className="p-3" style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)' }} data-testid="reply-composer">
                <textarea
                  className="input-agency text-sm resize-none mb-2"
                  rows={3}
                  placeholder={alsoEmail ? 'Type your email reply…' : 'Type a chat reply…'}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  data-testid="reply-textarea"
                />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: 'var(--muted)' }}>
                    <input type="checkbox" checked={alsoEmail} onChange={e => setAlsoEmail(e.target.checked)} data-testid="also-email-toggle" />
                    Also send as email {active.channel === 'email' ? '(recommended)' : ''}
                  </label>
                  <button
                    onClick={send}
                    disabled={sending || !reply.trim()}
                    className="btn-primary ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5"
                    data-testid="reply-send-button"
                  >
                    <Send size={11} /> {sending ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Assign-to-project modal */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="assign-modal">
          <div className="w-full max-w-md rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} style={{ color: '#006080' }} />
              <h3 className="font-black text-base" style={{ color: 'var(--ink)' }}>Assign thread to a project</h3>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
              Every message in this thread will move under the project you pick, and future email replies will thread correctly.
            </p>
            <select className="input-agency text-sm mb-3" value={assignTarget} onChange={e => setAssignTarget(e.target.value)} data-testid="assign-project-select">
              <option value="">Pick a project…</option>
              {projectsForAssign.map(p => (
                <option key={p.id} value={p.id}>{p.client_name ? `${p.client_name} — ` : ''}{p.name}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button className="flex-1 py-2 text-sm rounded-btn border" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setAssignOpen(false)}>Cancel</button>
              <button className="flex-1 btn-primary py-2 text-sm" onClick={doAssign} disabled={!assignTarget} data-testid="assign-confirm">Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
